/**
 * HazopWorksheet — B.6.1 per `Capexara Process Industry_ProcessPlant_Complete_Architecture.md` §6.1.
 *
 * Table-based worksheet with HAZOP nodes, guideword × parameter deviations,
 * cause/consequence/safeguards/risk/recommendation cells. Persists at
 * pdo.safety.hazop via PUT /projects/{id}/hazop-worksheet.
 */
import React, { useEffect, useState } from "react";
import { Loader2, Save, Plus, Trash2, Layers } from "lucide-react";
import api, { formatErr } from "../lib/api";

const GUIDEWORDS = ["NO", "MORE", "LESS", "AS WELL AS", "PART OF",
                     "REVERSE", "OTHER THAN", "EARLY", "LATE"];
const PARAMETERS = ["Flow", "Pressure", "Temperature", "Level",
                     "Composition", "Reaction", "Phase", "Time", "Sequence"];
const RISK_LEVELS = ["", "L", "M", "H"];

export default function HazopWorksheet({ projectId }) {
  const [busy, setBusy] = useState(false);
  const [nodes, setNodes] = useState([]);
  const [leader, setLeader] = useState("");
  const [date, setDate] = useState("");
  const [activeNode, setActiveNode] = useState(null);
  const [toast, setToast] = useState(null);

  const show = (t, kind = "ok") => {
    setToast({ t, kind });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        const r = await api.get(`/projects/${projectId}/hazop-worksheet`);
        setNodes(r.data?.nodes || []);
        setLeader(r.data?.study_leader || "");
        setDate(r.data?.study_date || "");
        setActiveNode(r.data?.nodes?.[0]?.node_id || null);
      } catch (e) {
        show(formatErr(e?.response?.data?.detail) || e.message, "err");
      } finally { setBusy(false); }
    })();
  }, [projectId]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/projects/${projectId}/hazop-worksheet`,
                                { nodes, study_leader: leader, study_date: date });
      show(`Saved — ${r.data.node_count} nodes, ${r.data.deviation_count} deviations`);
    } catch (e) {
      show(formatErr(e?.response?.data?.detail) || e.message, "err");
    } finally { setBusy(false); }
  };

  const addNode = () => {
    const node_id = `N${nodes.length + 1}`;
    const newNode = { node_id, node_name: `Node ${nodes.length + 1}`,
                        intent: "", deviations: [] };
    setNodes([...nodes, newNode]);
    setActiveNode(node_id);
  };

  const delNode = (id) => {
    if (!window.confirm(`Delete node ${id} and all its deviations?`)) return;
    const remaining = nodes.filter((n) => n.node_id !== id);
    setNodes(remaining);
    if (activeNode === id) setActiveNode(remaining[0]?.node_id || null);
  };

  const updateNode = (id, patch) =>
    setNodes(nodes.map((n) => n.node_id === id ? { ...n, ...patch } : n));

  const addDev = (nodeId) => {
    updateNode(nodeId, {
      deviations: [
        ...(nodes.find((n) => n.node_id === nodeId)?.deviations || []),
        { guideword: "NO", parameter: "Flow", cause: "", consequence: "",
          safeguards: "", risk_before: "M", risk_after: "L",
          recommendation: "", action_party: "" },
      ],
    });
  };
  const updateDev = (nodeId, idx, patch) => {
    const n = nodes.find((x) => x.node_id === nodeId);
    if (!n) return;
    const devs = n.deviations.map((d, i) => i === idx ? { ...d, ...patch } : d);
    updateNode(nodeId, { deviations: devs });
  };
  const delDev = (nodeId, idx) => {
    const n = nodes.find((x) => x.node_id === nodeId);
    if (!n) return;
    updateNode(nodeId, { deviations: n.deviations.filter((_, i) => i !== idx) });
  };

  const node = nodes.find((n) => n.node_id === activeNode);

  return (
    <div data-testid="hazop-worksheet"
         style={{ display: "flex", flexDirection: "column", gap: 10, padding: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Layers size={14} color="var(--pf-accent)" />
        <span className="pf-overline" style={{ margin: 0 }}>HAZOP WORKSHEET (B.6.1)</span>
        <input placeholder="Study leader"
                data-testid="hazop-study-leader"
                value={leader} onChange={(e) => setLeader(e.target.value)}
                style={inp} />
        <input type="date" data-testid="hazop-study-date"
                value={date} onChange={(e) => setDate(e.target.value)}
                style={inp} />
        <div style={{ flex: 1 }} />
        <button className="btn btn-primary" data-testid="hazop-save-btn"
                 onClick={save} disabled={busy}
                 style={btn}>
          {busy ? <Loader2 className="pf-spin" size={12}/> : <Save size={12}/>} Save
        </button>
      </div>

      {/* Node tabs */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap",
                     borderBottom: "1px solid var(--pf-border)",
                     paddingBottom: 6 }}>
        {nodes.map((n) => (
          <button key={n.node_id}
                   data-testid={`hazop-node-tab-${n.node_id}`}
                   className="btn btn-ghost"
                   onClick={() => setActiveNode(n.node_id)}
                   style={{
                     ...btn,
                     background: activeNode === n.node_id
                       ? "var(--pf-accent)" : "var(--pf-surface)",
                     color: activeNode === n.node_id
                       ? "#0E1218" : "var(--pf-fg)",
                     fontWeight: activeNode === n.node_id ? 700 : 500,
                   }}>
            {n.node_id} · {n.node_name}
          </button>
        ))}
        <button className="btn btn-ghost" data-testid="hazop-add-node"
                 onClick={addNode}
                 style={{ ...btn, borderStyle: "dashed" }}>
          <Plus size={12} /> Add Node
        </button>
      </div>

      {node && (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input data-testid={`hazop-node-${node.node_id}-name`}
                    value={node.node_name}
                    onChange={(e) => updateNode(node.node_id, { node_name: e.target.value })}
                    placeholder="Node name"
                    style={{ ...inp, minWidth: 200 }} />
            <input data-testid={`hazop-node-${node.node_id}-intent`}
                    value={node.intent || ""}
                    onChange={(e) => updateNode(node.node_id, { intent: e.target.value })}
                    placeholder="Design intent"
                    style={{ ...inp, flex: 1 }} />
            <button className="btn btn-ghost"
                     data-testid={`hazop-node-${node.node_id}-delete`}
                     onClick={() => delNode(node.node_id)}
                     style={{ ...btn, color: "#ef4444" }}>
              <Trash2 size={12} /> Delete node
            </button>
          </div>

          <div style={{ overflow: "auto", border: "1px solid var(--pf-border)",
                         borderRadius: 4 }}>
            <table style={{ width: "100%", borderCollapse: "collapse",
                              fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>
              <thead style={{ background: "var(--pf-surface-2)" }}>
                <tr>
                  {["Guideword", "Parameter", "Cause", "Consequence", "Safeguards",
                    "Risk B", "Recommendation", "Action Party", "Risk A", ""]
                    .map((h) => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(node.deviations || []).map((d, idx) => (
                  <tr key={idx} data-testid={`hazop-dev-row-${node.node_id}-${idx}`}>
                    <td style={td}>
                      <select value={d.guideword}
                               onChange={(e) => updateDev(node.node_id, idx, { guideword: e.target.value })}
                               style={tdInp}
                               data-testid={`hazop-dev-${idx}-gw`}>
                        {GUIDEWORDS.map((g) => <option key={g}>{g}</option>)}
                      </select>
                    </td>
                    <td style={td}>
                      <select value={d.parameter}
                               onChange={(e) => updateDev(node.node_id, idx, { parameter: e.target.value })}
                               style={tdInp}
                               data-testid={`hazop-dev-${idx}-param`}>
                        {PARAMETERS.map((p) => <option key={p}>{p}</option>)}
                      </select>
                    </td>
                    <td style={td}><textarea style={cellInp} rows={2}
                                    data-testid={`hazop-dev-${idx}-cause`}
                                    value={d.cause}
                                    onChange={(e) => updateDev(node.node_id, idx, { cause: e.target.value })}/></td>
                    <td style={td}><textarea style={cellInp} rows={2}
                                    data-testid={`hazop-dev-${idx}-cons`}
                                    value={d.consequence}
                                    onChange={(e) => updateDev(node.node_id, idx, { consequence: e.target.value })}/></td>
                    <td style={td}><textarea style={cellInp} rows={2}
                                    data-testid={`hazop-dev-${idx}-safe`}
                                    value={d.safeguards}
                                    onChange={(e) => updateDev(node.node_id, idx, { safeguards: e.target.value })}/></td>
                    <td style={td}>
                      <select value={d.risk_before || ""}
                               onChange={(e) => updateDev(node.node_id, idx, { risk_before: e.target.value })}
                               style={tdInp}>
                        {RISK_LEVELS.map((r) => <option key={r}>{r}</option>)}
                      </select>
                    </td>
                    <td style={td}><textarea style={cellInp} rows={2}
                                    data-testid={`hazop-dev-${idx}-rec`}
                                    value={d.recommendation}
                                    onChange={(e) => updateDev(node.node_id, idx, { recommendation: e.target.value })}/></td>
                    <td style={td}><input style={cellInp}
                                    data-testid={`hazop-dev-${idx}-ap`}
                                    value={d.action_party}
                                    onChange={(e) => updateDev(node.node_id, idx, { action_party: e.target.value })}/></td>
                    <td style={td}>
                      <select value={d.risk_after || ""}
                               onChange={(e) => updateDev(node.node_id, idx, { risk_after: e.target.value })}
                               style={tdInp}>
                        {RISK_LEVELS.map((r) => <option key={r}>{r}</option>)}
                      </select>
                    </td>
                    <td style={td}>
                      <button className="btn btn-ghost"
                               data-testid={`hazop-dev-${idx}-del`}
                               onClick={() => delDev(node.node_id, idx)}
                               style={{ ...btn, color: "#ef4444", padding: "2px 4px" }}>
                        <Trash2 size={10}/>
                      </button>
                    </td>
                  </tr>
                ))}
                {!(node.deviations || []).length && (
                  <tr>
                    <td colSpan={10} style={{ ...td, textAlign: "center",
                                                  color: "var(--pf-text-dim)" }}>
                      No deviations yet — click "Add deviation" below.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <button className="btn btn-ghost"
                   data-testid={`hazop-add-dev-${node.node_id}`}
                   onClick={() => addDev(node.node_id)}
                   style={{ ...btn, alignSelf: "flex-start", borderStyle: "dashed" }}>
            <Plus size={12} /> Add deviation
          </button>
        </>
      )}

      {!nodes.length && !busy && (
        <div style={{
          textAlign: "center", padding: "40px 20px",
          color: "var(--pf-text-dim)",
          fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
        }}>
          No HAZOP nodes defined. Click "Add Node" to create the first study node.
        </div>
      )}

      {toast && (
        <div data-testid={`hazop-toast-${toast.kind}`} style={{
          position: "fixed", bottom: 28, right: 28,
          background: toast.kind === "ok" ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)",
          color: toast.kind === "ok" ? "#86efac" : "#fca5a5",
          border: `1px solid ${toast.kind === "ok" ? "#22c55e" : "#ef4444"}`,
          padding: "8px 14px", fontSize: 12, borderRadius: 3, zIndex: 10001,
          fontFamily: "'JetBrains Mono', monospace",
        }}>{toast.t}</div>
      )}
    </div>
  );
}

const inp = { background: "var(--pf-surface-2)", border: "1px solid var(--pf-border)",
               color: "var(--pf-fg)", padding: "4px 8px", fontSize: 11,
               borderRadius: 3, fontFamily: "'JetBrains Mono', monospace" };
const btn = { fontSize: 11, padding: "4px 10px", borderRadius: 3 };
const th = { padding: "6px 8px", textAlign: "left", color: "var(--pf-text-dim)",
              borderBottom: "1px solid var(--pf-border)", fontSize: 10,
              whiteSpace: "nowrap", textTransform: "uppercase" };
const td = { padding: 4, borderBottom: "1px solid var(--pf-border-dim, #222)",
              verticalAlign: "top" };
const tdInp = { ...inp, width: "100%", fontSize: 10, padding: "2px 4px" };
const cellInp = { ...inp, width: "100%", minWidth: 120, fontSize: 10, padding: "2px 4px",
                   resize: "vertical" };
